import CallError from '../../../utils/CallError';
import { getBadParams, getMissingKeys } from '../../../utils/general';
import { reviewSettings } from '../setup';

/**
 * @typedef ReplyObject
 * @type {Object}
 * @property {bool} authorIsDeveloper
 * @property {string} authorName
 * @property {string} createdAt
 * @property {string} editedAt
 * @property {string} id
 * @property {bool} isEditable
 * @property {string} reviewId
 * @property {string} title
 * @property {string} text
 *
 * @typedef PageSpec
 * @type {Object}
 * @property {number} count
 * @property {bool} nextPageAvailable
 * @property {number} offset
 * @property {number} totalItems
 */

/**
 * Gets replies for a review
 * @param {object} params - an object of params
 * @param {string} params.reviewId - ID of the review you want the replies for
 * @param {number} [params.limit=5] - Works as page size
 * @param {number} [params.offset=0] - Offset of the results (works as pageIndex)
 * @param {AbortSignal} [signal] - Abort signal that can be used to abort the fetch request
 * @returns {Promise<{items: ReplyObject[], pageSpec: PageSpec}>}
 * @throws {CallError}
 */
export default async (params = {}, signal = null) => {
  const requiredParams = ['reviewId'];
  const paramsRules = {
    reviewId: { allowedTypes: ['string'] },
    userId: { allowedTypes: ['string'] },
    accessToken: { allowedTypes: ['string'] },
    limit: { allowedTypes: ['number'] },
    offset: { allowedTypes: ['number'] },
  };
  const missingParams = getMissingKeys(params, requiredParams);
  let badParams = [];
  if (Object.keys(params).length) {
    badParams = getBadParams(params, paramsRules);
  }
  if (missingParams.length || badParams.length) {
    throw new CallError('params', null, 404, { missingParams, badParams });
  }

  const { reviewId, limit = 5, offset = 0, userId, accessToken } = params;
  const isSignedIn = accessToken && userId;
  const endpoint = reviewSettings.endpoint[isSignedIn ? 'get-replies-auth' : 'get-reply'];
  if (!endpoint) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR get-replies endpoint' });
  }
  if (!reviewSettings.apiKey) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR api key' });
  }

  const searchParams = new URLSearchParams({
    limit,
    offset,
    orderBy: '+lastUpdated',
  });
  searchParams.sort();
  const requestUrl = endpoint.replace('{reviewId}', reviewId);
  const response = await fetch(`${requestUrl}?${searchParams.toString()}`, {
    signal,
    method: 'GET',
    headers: {
      authorization: accessToken,
      accept: isSignedIn
        ? 'application/vnd.adobe-review.review-replies-data-detailed-auth-v1+json'
        : 'application/vnd.adobe-review.review-replies-data-detailed-v1+json',
      'x-api-key': reviewSettings.apiKey,
      'x-gw-ims-user-id': userId,
    },
  });
  /**
   * @type {{items: ReplyObject[], pageSpec: PageSpec}}
   */
  let content = await response.text();
  try {
    content = JSON.parse(content);
  } catch (e) {
    // response is not json
  }

  if (!response.ok) {
    throw new CallError('http', content, response.status, { message: response.statusText });
  } else {
    return content;
  }
};
